import React, { useEffect, useMemo, useState } from 'react';
import { useWordContext } from '../context/WordContext';
import apiClient from '../utils/apiClient';

const HanziFlashcards = () => {
  const {
    savedHanzi,
    updateHanziGrade,
    updateHanziKnown,
    savedHanziDetails
  } = useWordContext();

  const [hanziDetails, setHanziDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tabs: 'study' | 'saved' | 'known'
  const [activeTab, setActiveTab] = useState('saved');

  // Session controls
  const [repeatsPerHanzi, setRepeatsPerHanzi] = useState(1);
  const [threshold, setThreshold] = useState(3);
  const [round, setRound] = useState(1);

  // Study deck state
  const [queue, setQueue] = useState([]); // array of hanzi chars (with repeats)
  const [currentIndex, setCurrentIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);

  // Track scores this round: {char: [scores...]}
  const [roundScores, setRoundScores] = useState({});

  // Saved/Known selection for definition reveal
  const [selectedChar, setSelectedChar] = useState(null);

  // Build known and study lists from savedHanzi using details
  const knownHanzi = useMemo(() => savedHanzi.filter(h => savedHanziDetails[h]?.known), [savedHanzi, savedHanziDetails]);
  const studyHanzi = useMemo(() => savedHanzi.filter(h => !savedHanziDetails[h]?.known), [savedHanzi, savedHanziDetails]);

  // Load hanzi details (pinyin/definition) from API for all saved hanzi
  useEffect(() => {
    const loadDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!savedHanzi || savedHanzi.length === 0) {
          setHanziDetails({});
          setLoading(false);
          return;
        }
        const unique = Array.from(new Set(savedHanzi));
        const result = await apiClient.post('/api/hanzi-details', { characters: unique });
        setHanziDetails(result || {});
      } catch (e) {
        setError('Failed to load Hanzi details');
      } finally {
        setLoading(false);
      }
    };
    loadDetails();
  }, [savedHanzi]);

  // Start a new session with current studyHanzi
  const startSession = () => {
    const base = studyHanzi;
    const repeated = [];
    for (let i = 0; i < Math.max(1, Number(repeatsPerHanzi)); i++) {
      repeated.push(...base);
    }
    // simple shuffle
    for (let i = repeated.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [repeated[i], repeated[j]] = [repeated[j], repeated[i]];
    }
    setQueue(repeated);
    setCurrentIndex(0);
    setRevealed(false);
    setRoundScores({});
    setRound(1);
    setActiveTab('study');
  };

  const total = queue.length;
  const progress = total === 0 ? 0 : Math.round(((currentIndex + 1) / total) * 100);
  const currentChar = queue[currentIndex];
  const currentInfo = currentChar ? hanziDetails[currentChar] : null;

  const handleReveal = () => setRevealed(r => !r);

  const handleRate = async (score) => {
    if (!currentChar) return;
    // update DB grade immediately
    await updateHanziGrade(currentChar, score);
    // Update round scores
    setRoundScores(prev => {
      const list = prev[currentChar] ? [...prev[currentChar], score] : [score];
      return { ...prev, [currentChar]: list };
    });
    // Advance
    const nextIndex = currentIndex + 1;
    if (nextIndex < queue.length) {
      setCurrentIndex(nextIndex);
      setRevealed(false);
    } else {
      // round complete
      setRevealed(false);
    }
  };

  const handleKnownToggle = async (checked) => {
    if (!currentChar) return;
    await updateHanziKnown(currentChar, checked);
    const nextIndex = currentIndex + 1;
    if (nextIndex < queue.length) {
      setCurrentIndex(nextIndex);
      setRevealed(false);
    }
  };

  // Compute per-hanzi average for round
  const roundAverages = useMemo(() => {
    const result = {};
    Object.entries(roundScores).forEach(([char, scores]) => {
      if (scores && scores.length) {
        const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
        result[char] = avg;
      }
    });
    return result;
  }, [roundScores]);

  // Start next round with hanzi below threshold
  const startNextRound = () => {
    const below = Object.entries(roundAverages)
      .filter(([, avg]) => avg < Number(threshold))
      .map(([ch]) => ch)
      .filter(ch => !savedHanziDetails[ch]?.known);

    if (below.length === 0) {
      setQueue([]);
      setCurrentIndex(0);
      setRoundScores({});
      setRound(r => r + 1);
      return;
    }

    const repeated = [];
    for (let i = 0; i < Math.max(1, Number(repeatsPerHanzi)); i++) {
      repeated.push(...below);
    }
    for (let i = repeated.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [repeated[i], repeated[j]] = [repeated[j], repeated[i]];
    }
    setQueue(repeated);
    setCurrentIndex(0);
    setRevealed(false);
    setRoundScores({});
    setRound(r => r + 1);
  };

  const renderStickyHeader = () => (
    <div className="hanziStickyHeader">
      <h1 className="page-title" style={{ margin: 0 }}>Hanzi Flashcards</h1>
      <div className="hanziTabs">
        <button className={`hanziTab ${activeTab === 'study' ? 'active' : ''}`} onClick={() => setActiveTab('study')}>Study</button>
        <button className={`hanziTab ${activeTab === 'saved' ? 'active' : ''}`} onClick={() => setActiveTab('saved')}>Saved</button>
        <button className={`hanziTab ${activeTab === 'known' ? 'active' : ''}`} onClick={() => setActiveTab('known')}>Known</button>
      </div>
    </div>
  );

  const renderControls = () => (
    <div className="hanziSessionWrapper story-container">
      <div className="hanziFlashcardsControls audioPlayerSettingsPanel" style={{ margin: 0 }}>
        <div className="audioPlayerSettingsTitle">Session Settings</div>
        <div className="audioPlayerSettingRow">
          <label className="audioPlayerSettingLabel">Repeats per Hanzi</label>
          <input
            type="number"
            min={1}
            max={10}
            value={repeatsPerHanzi}
            onChange={e => setRepeatsPerHanzi(Number(e.target.value) || 1)}
            className="feature-requests-input"
            style={{ maxWidth: 120 }}
          />
        </div>
        <div className="audioPlayerSettingRow">
          <label className="audioPlayerSettingLabel">Next round: include scores lower than</label>
          <input
            type="number"
            min={1}
            max={5}
            value={threshold}
            onChange={e => setThreshold(Number(e.target.value) || 1)}
            className="feature-requests-input"
            style={{ maxWidth: 120 }}
          />
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="reload-button" onClick={startSession}>Start Session</button>
          {queue.length > 0 && currentIndex < queue.length && (
            <button className="reload-button" onClick={() => { setQueue([]); setCurrentIndex(0); setRoundScores({}); }}>End Session</button>
          )}
        </div>
      </div>
    </div>
  );

  const renderFlashcard = () => {
    if (!currentChar) return null;
    return (
      <div className="hanziFlashcard story-container" onClick={handleReveal}>
        <div className="hanziFlashcardHeader">
          <div className="audioPlayerTrackHeader">Round {round}</div>
          <div className="hanziFlashcardProgress">
            <div className="audioPlayerProgressBarContainer">
              <div className="audioPlayerProgressBar" style={{ width: `${progress}%` }}></div>
            </div>
            <div className="audioPlayerTrackHeader" style={{ fontWeight: 'normal' }}>{Math.min(currentIndex + 1, total)} / {total}</div>
          </div>
        </div>
        <div className="hanziFlashcardCharacter">{currentChar}</div>
        {!revealed && (
          <div className="instruction" style={{ color: '#fff', textAlign: 'center' }}>Click to reveal details</div>
        )}
        {revealed && (
          <div className="hanziFlashcardDetails">
            <div className="details-grid">
              {currentInfo?.pinyin && (
                <div className="detail-item">
                  <div className="detail-label">Pinyin</div>
                  <div className="detail-value">{currentInfo.pinyin}</div>
                </div>
              )}
              {currentInfo?.definition && (
                <div className="detail-item">
                  <div className="detail-label">Definition</div>
                  <div className="detail-value">{currentInfo.definition}</div>
                </div>
              )}
              {currentInfo?.hsk_level && (
                <div className="detail-item">
                  <div className="detail-label">HSK Level</div>
                  <div className="detail-value">{currentInfo.hsk_level}</div>
                </div>
              )}
              {currentInfo?.stroke_count && (
                <div className="detail-item">
                  <div className="detail-label">Strokes</div>
                  <div className="detail-value">{currentInfo.stroke_count}</div>
                </div>
              )}
            </div>
          </div>
        )}
        {revealed && (
          <div className="hanziFlashcardActions">
            <div className="hanziRating">
              {[1,2,3,4,5].map(n => (
                <button key={n} className="audioPlayerButton" onClick={(e) => { e.stopPropagation(); handleRate(n); }}>{n}</button>
              ))}
            </div>
            <label className="audioPlayerSettingLabel" style={{ display: 'flex', alignItems: 'center', gap: 8 }} onClick={e => e.stopPropagation()}>
              <input type="checkbox" onChange={e => handleKnownToggle(e.target.checked)} />
              I know it
            </label>
          </div>
        )}
      </div>
    );
  };

  const renderCompletion = () => {
    if (queue.length === 0 || currentIndex < queue.length - 1) return null;
    const items = Object.keys(roundAverages);
    return (
      <div className="story-container" style={{ textAlign: 'center' }}>
        <h2 className="audioPlayerPlaylistName">Round Completed</h2>
        {items.length === 0 ? (
          <div className="loading-text">No scores recorded.</div>
        ) : (
          <div style={{ color: '#fff' }}>
            {items.map(ch => (
              <div key={ch} style={{ marginBottom: 8 }}>
                <strong style={{ fontSize: 22 }}>{ch}</strong> � avg {roundAverages[ch].toFixed(2)}
              </div>
            ))}
          </div>
        )}
        <div className="audioPlayerSettingsPanel" style={{ marginTop: 20 }}>
          <div className="audioPlayerSettingsTitle">Next Round</div>
          <div className="audioPlayerSettingRow">
            <label className="audioPlayerSettingLabel">Include hanzi with average lower than</label>
            <input
              type="number"
              min={1}
              max={5}
              value={threshold}
              onChange={e => setThreshold(Number(e.target.value) || 1)}
              className="feature-requests-input"
              style={{ maxWidth: 120 }}
            />
          </div>
          <button className="reload-button" onClick={startNextRound}>Start Round {round + 1}</button>
        </div>
      </div>
    );
  };

  const renderTileGrid = (chars) => (
    <div className="story-container">
      <div className="audioPlayerTrackHeader" style={{ marginBottom: 10 }}>{activeTab === 'known' ? 'Known Hanzi' : 'Saved Hanzi'}</div>
      <div className="hanziTileGrid">
        {chars.map(ch => (
          <button
            key={ch}
            className={`hanziTile ${selectedChar === ch ? 'active' : ''}`}
            onClick={() => setSelectedChar(prev => (prev === ch ? null : ch))}
            aria-label={`character ${ch}`}
          >
            {ch}
          </button>
        ))}
      </div>
      {selectedChar && hanziDetails[selectedChar] && (
        <div className="hanziDetailsPanel">
          <div className="audioPlayerTrackHeader" style={{ marginBottom: 8 }}>Details</div>
          <div className="details-grid">
            {hanziDetails[selectedChar]?.pinyin && (
              <div className="detail-item">
                <div className="detail-label">Pinyin</div>
                <div className="detail-value">{hanziDetails[selectedChar].pinyin}</div>
              </div>
            )}
            {hanziDetails[selectedChar]?.definition && (
              <div className="detail-item">
                <div className="detail-label">Definition</div>
                <div className="detail-value">{hanziDetails[selectedChar].definition}</div>
              </div>
            )}
            {hanziDetails[selectedChar]?.hsk_level && (
              <div className="detail-item">
                <div className="detail-label">HSK Level</div>
                <div className="detail-value">{hanziDetails[selectedChar].hsk_level}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="playlistsContainer">
      {renderStickyHeader()}

      {loading && <div className="loading-text">Loading your saved Hanzi�</div>}
      {error && <div className="error-text">{error}</div>}

      {!loading && savedHanzi.length === 0 && (
        <div className="story-container">
          <div className="loading-text">You have no saved Hanzi yet. Save characters from Cards, Stories, or Bojee to study them here.</div>
        </div>
      )}

      {!loading && savedHanzi.length > 0 && (
        <>
          {activeTab === 'study' && (
            <>
              {renderControls()}
              {queue.length === 0 ? (
                <div className="story-container" style={{ textAlign: 'center' }}>
                  <div className="audioPlayerTrackHeader">Study Deck ({studyHanzi.length})</div>
                  <div className="loading-text">Press Start Session to begin</div>
                </div>
              ) : (
                <>
                  {renderFlashcard()}
                  {renderCompletion()}
                </>
              )}
            </>
          )}

          {activeTab === 'saved' && renderTileGrid(studyHanzi)}
          {activeTab === 'known' && renderTileGrid(knownHanzi)}
        </>
      )}
    </div>
  );
};

export default HanziFlashcards;
